import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import { DataGrid, GridColDef, GridColumnMenu, GridColumnMenuProps, GridPaginationModel } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import styled from 'styled-components';
import { Button, Stack, Typography } from '@mui/material';
import { ContainerEditGroup } from '../../../pages/settings/styles';
import { GroupDataFromSearchResult, GroupMemberDataFromSearchResult } from '../../../interfaces/group.interface';
import { GroupStatus } from '../../../enums/group';
import { FilterAlt, FilterList } from '@mui/icons-material';
import { formatDate, getDataGridLocaleText } from '../../../i18n/formatting';
import { useAppLocale } from '../../../i18n/useAppLocale';

interface GroupTableProps {
  groups: GroupDataFromSearchResult[];
  totalItems: number;
  currentPage: number;
  searchTerm?: string;
  setModalEditGroupData: (modalDataState: {
    show: boolean;
    group_id: string,
    group_name: string,
    group_status: GroupStatus,
    group_members: GroupMemberDataFromSearchResult[],
    responsible_users: GroupMemberDataFromSearchResult[],
  }) => void;
  onPageChange: (newPage: number) => void;
}

const getGroupRowId = (row: { id: string }) => row.id;

const ListTable = styled.div`
  width: 100%;

  .MuiDataGrid-root {
    font-size: 16px;
    background: #FFF;
    border-radius: 5px;
    border: none;
    width: 100%;
  }

  .MuiDataGrid-columnHeaders {
    background-color: #F1F1F1;
    font-weight: bold;
    color: #878B8E;
    border-bottom: 3px solid #F1F1F1;
  }

  .MuiDataGrid-cell {
    color: #878B8E;
    font-size: 16px;
    overflow: hidden;
  }

  .MuiDataGrid-row:hover {
    background-color: transparent !important;
  }

  .MuiDataGrid-cell:focus,
  .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  .MuiDataGrid-cell:focus-within,
  .MuiDataGrid-columnHeader:focus-within {
    outline: none !important;
  }
`;

const GroupTable: React.FC<GroupTableProps> = ({
  groups,
  currentPage,
  onPageChange,
  searchTerm = '',
  setModalEditGroupData,
}) => {
  const appLocale = useAppLocale();
  const { t } = useTranslation('settings');
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: String(t('table.groupName')),
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.4,
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Avatar
            sx={{
              background: 'linear-gradient(318deg, #00B2B5 16.44%, #96FFD2 85.22%)',
              color: '#3C4043',
              width: 35,
              height: 35,
              fontSize: '14px',
              flexShrink: 0,
            }}
          >
            {params.row.name.charAt(0).toUpperCase()}
          </Avatar>

          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: 1,
              minWidth: 0,
            }}
            title={params.row.name}
          >
            {params.row.name}
          </Box>
        </Box>
      )
    },
    {
      field: 'created_at',
      headerName: String(t('table.createdAt')),
      type: 'date',
      flex: 1,
      minWidth: 170,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value) => {
        if (!value) return null;

        const date = new Date(value as string);
        return Number.isNaN(date.getTime()) ? null : date;
      },
      valueFormatter: (value: any) => {
        if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
          return ' - - ';
        }

        return formatDate(value, appLocale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
      renderCell: (params) => (
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
            minWidth: 0,
            textAlign: 'center',
          }}
          title={String(params.formattedValue || ' - - ')}
        >
          {String(params.formattedValue || ' - - ')}
        </Box>
      )
    },
    {
      field: 'quantity_members',
      headerName: String(t('table.memberCount')),
      type: 'number',
      flex: 1,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
            minWidth: 0,
            textAlign: 'center',
          }}
          title={String(params.value ?? 0)}
        >
          {params.value ?? 0}
        </Box>
      )
    },
    {
      field: 'edit',
      headerName: '',
      flex: 1,
      minWidth: 250,
      align: 'center',
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      renderCell: (params) => (
        <ContainerEditGroup>
          <Button
            variant="contained"
            sx={{
              boxShadow: 'none',
              backgroundColor: '#BBE2F8',
              color: '#4F89BE',
              fontWeight: '500',
              textTransform: 'none',
              fontSize: '1em',
              padding: '.5em 1.5em',
              borderRadius: '2em',
            }}
            startIcon={<EditIcon sx={{ fontSize: 20 }} />}
            onClick={() =>
              setModalEditGroupData({
                show: true,
                group_id: params.row.id,
                group_name: params.row.name,
                group_members: params.row.users,
                group_status: params.row.status,
                responsible_users: params.row.responsible_users || [],
              })
            }
          >
            Editar grupo
          </Button>
        </ContainerEditGroup>
      )
    },
  ];

  const rows = useMemo(
    () =>
      groups.map((group, index) => ({
        id: group.id,
        name: group.name,
        created_at: group.created_at,
        updated_at: group.updated_at,
        quantity_members: group.quantity_members,
        status: group.status,
        users: group.users,
        responsible_users: group.responsible_users || [],
        index: index + 1,
      })),
    [groups]
  );

  const singlePageSize = useMemo(() => {
    if (rows.length <= 0) return 1;
    return Math.min(rows.length, 100);
  }, [rows.length]);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: Math.max(currentPage - 1, 0),
    pageSize: singlePageSize,
  });

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: singlePageSize,
    });
  }, [singlePageSize]);

  const handlePaginationChange = (newModel: GridPaginationModel) => {
    const nextPage = rows.length <= singlePageSize ? 0 : newModel.page;

    setPaginationModel({
      page: nextPage,
      pageSize: singlePageSize,
    });

    onPageChange(nextPage + 1);
  };

  function CustomColumnMenu(props: GridColumnMenuProps) {
    return (
      <GridColumnMenu
        {...props}
        slots={{
          columnMenuSortItem: null
        }}
      />
    );
  }

  const hasActiveSearch = searchTerm.trim().length > 0;
  
  const ActiveColumnFilteredIcon = () => (
    <FilterAlt sx={{ color: '#4A90E2', fontSize: 18 }} />
  );

  const SearchAwareOverlay = () => (
    <Stack
      sx={{
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2,
        textAlign: 'center',
      }}
      spacing={1}
    >
      <Typography variant="subtitle1" sx={{ color: '#3C4043', fontWeight: 600 }}>
        {hasActiveSearch
          ? 'Nenhum resultado encontrado com os filtros aplicados'
          : 'Nenhum grupo encontrado'}
      </Typography>
      <Typography variant="body2" sx={{ color: '#878B8E' }}>
        {hasActiveSearch
          ? 'Ajuste ou limpe os filtros para encontrar grupos que correspondam aos critérios de busca.'
          : 'Não foi encontrado nenhum grupo que corresponda aos critérios de busca.'}
      </Typography>
    </Stack>
  );

  const NoRowsOverlay = () => (
    <Stack
      sx={{
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
        px: 2,
        textAlign: 'center',
      }}
      spacing={1}
    >
      <Typography variant="subtitle1" sx={{ color: '#3C4043', fontWeight: 600 }}>
        Nenhum grupo encontrado
      </Typography>
      <Typography variant="body2" sx={{ color: '#878B8E' }}>
        Não foi encontrado nenhum grupo que corresponda aos critérios de busca.
      </Typography>
    </Stack>
  );
  void NoRowsOverlay;

  return (
    <ListTable>
      <Box sx={{ width: '100%', overflowX: 'auto', overflowY: 'visible' }}>
        <DataGrid
          autoHeight
          rowHeight={84}
          columnHeaderHeight={66}
          rows={rows}
          columns={columns}
          getRowId={getGroupRowId}
          localeText={getDataGridLocaleText(appLocale)}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          pageSizeOptions={[singlePageSize]}
          disableRowSelectionOnClick
          disableAutosize
          disableMultipleRowSelection
          disableColumnSelector
          hideFooterSelectedRowCount
          slots={{
            columnMenu: CustomColumnMenu,
            columnMenuIcon: FilterList,
            columnFilteredIcon: ActiveColumnFilteredIcon,
            noRowsOverlay: SearchAwareOverlay,
            noResultsOverlay: SearchAwareOverlay,
          }}
          slotProps={{
            basePopper: {
              sx: {
                '& .MuiPaper-root': {
                  borderRadius: '8px',
                  boxShadow: '0px 10px 30px rgba(0,0,0,0.15)',
                  border: '1px solid #ECEFF3',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  maxWidth: 'calc(100vw - 48px)',
                },
                '& .MuiDataGrid-panelWrapper': {
                  padding: '8px',
                  minWidth: '760px',
                },
                '& .MuiDataGrid-panelContent': {
                  overflow: 'visible !important',
                },
                '& .MuiDataGrid-filterForm': {
                  gap: '12px',
                  padding: '10px',
                  borderRadius: '12px',
                  marginBottom: '8px',
                  minWidth: '720px',
                  width: 'max-content',
                },
                '& .MuiDataGrid-filterFormColumnInput': {
                  minWidth: '190px',
                  flex: '0 0 190px',
                },
                '& .MuiDataGrid-filterFormOperatorInput': {
                  minWidth: '220px',
                  flex: '0 0 220px',
                },
                '& .MuiDataGrid-filterFormValueInput': {
                  minWidth: '260px',
                  flex: '0 0 260px',
                },
                '& .MuiInputBase-root': {
                  borderRadius: '10px',
                },
                '& .MuiFormLabel-root': {
                  fontWeight: 500,
                },
                '& .MuiDataGrid-panelFooter': {
                  padding: '8px 12px 12px',
                },

                /* cria "área rolável extra" real à direita */
                '& .MuiDataGrid-filterForm::after': {
                  content: '""',
                  display: 'block',
                  flex: '0 0 24px',
                  width: '24px',
                },

                '@media (max-width: 768px)': {
                  '& .MuiDataGrid-filterForm::after': {
                    flex: '0 0 12px',
                    width: '12px',
                  },
                },
              },
            },
          }}
          sx={{
            minWidth: 0,
            '& .MuiSelect-select.MuiTablePagination-select': { display: 'none' },
            '& .MuiInputBase-root.MuiTablePagination-input': { display: 'none' },
            '& .MuiTablePagination-selectLabel': { display: 'none !important' },
            '& .MuiDataGrid-footerContainer p': { margin: 1 },
            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
            '& .MuiDataGrid-columnHeader': {
              fontSize: '16px',
              fontWeight: 'bold',
              padding: '1em 1.2em',
              color: '#878B8E',
            },
            '& .MuiDataGrid-cell': {
              padding: '1em 1.2em',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflowY: 'visible !important',
            },
            '& .MuiDataGrid-main': {
              overflow: 'visible',
            },
          }}
        />
      </Box>
    </ListTable>
  );
};

export default GroupTable;
